"use client"

// TechBlackHole — three.js fullscreen shader background.
// Ported onto the GPU platform (backend:'canvas', bring-your-own three.js
// renderer). The platform now owns the frame loop (the old bare rAF was never
// cancelled — it kept rendering a disposed renderer after unmount) and gates it
// by visibility. Teardown disposes the renderer AND forces context loss.

import * as React from "react"
import { useRef } from "react"
import {
  Clock,
  Mesh,
  PerspectiveCamera,
  PlaneGeometry,
  Scene,
  ShaderMaterial,
  Vector2,
  Vector3,
  WebGLRenderer,
} from "three"

import { useGpuEffect, type CanvasSetupContext } from "@/core/gpu/use-gpu-effect"

interface TechBlackHoleProps {
  offsetX?: number
  offsetY?: number
}

const VERT = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position, 1.0); }
`

const FRAG = `
  uniform float iTime;
  uniform vec3 iResolution;
  uniform vec2 iOffset;
  varying vec2 vUv;
  float hash(vec2 p) { p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
  float noise(vec2 p) {
    vec2 i = floor(p); vec2 f = fract(p); f = f * f * (3.0 - 2.0 * f);
    float a = hash(i); float b = hash(i + vec2(1.0, 0.0));
    float c = hash(i + vec2(0.0, 1.0)); float d = hash(i + vec2(1.0, 1.0));
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }
  float fbm(vec2 p) { float v = 0.0; float a = 0.5; for(int i = 0; i < 5; i++) { v += a * noise(p); p *= 2.0; a *= 0.5; } return v; }
  vec3 palette(float t) {
    vec3 a = vec3(0.5); vec3 b = vec3(0.5); vec3 c = vec3(1.0); vec3 d = vec3(0.0, 0.33, 0.67);
    return a + b * cos(6.28318 * (c * t + d));
  }
  void main() {
    vec2 uv = (gl_FragCoord.xy - 0.5 * iResolution.xy) / min(iResolution.y, iResolution.x);
    uv -= iOffset;
    float t = iTime * 0.15;
    float r = length(uv);
    float digiR = floor(r * 50.0) / 50.0;
    float angle = -t * 0.8 - 1.5 / (r + 0.05);
    float s = sin(angle); float c = cos(angle);
    mat2 rot = mat2(c, -s, s, c);
    vec2 rotatedUV = rot * uv;
    float n = fbm(vec2(length(uv) * 15.0 - t * 8.0, atan(rotatedUV.y, rotatedUV.x) * 6.0));
    vec2 noiseUV = rotatedUV * (8.0 + 12.0 * (1.0-smoothstep(0.0, 1.0, r)));
    float swirlNoise = fbm(noiseUV + t);
    float lines = step(0.95, fract(n * 5.0 + t));
    float holeRadius = 0.25;
    float holeEdge = smoothstep(holeRadius, holeRadius + 0.02, r);
    float diskFade = smoothstep(1.2, 0.35, r);
    float diskSharp = smoothstep(holeRadius, holeRadius + 0.05, r);
    float intensity = diskFade * diskSharp;
    float streaks = smoothstep(0.4, 0.8, n);
    vec3 col = palette(r * 3.0 + t * 0.5 + n * 0.2);
    col = mix(col, vec3(0.0, 1.0, 1.0), streaks * 0.5);
    col = mix(col, vec3(0.8, 0.9, 1.0), exp(-(r - holeRadius) * 20.0) * holeEdge);
    col *= intensity * (0.6 + 0.4 * streaks + 0.2 * lines);
    col *= holeEdge;
    float glow = exp(-r * 2.5) * 0.2 * holeEdge;
    col += vec3(0.1, 0.2, 0.5) * glow;
    float brightness = dot(col, vec3(0.299, 0.587, 0.114));
    float alpha = smoothstep(0.01, 0.1, brightness);
    alpha *= smoothstep(1.4, 0.5, r);
    gl_FragColor = vec4(col, alpha);
  }
`

class BlackHoleGL {
  private renderer: WebGLRenderer
  private scene: Scene
  private camera: PerspectiveCamera
  private geometry: PlaneGeometry
  private material: ShaderMaterial
  private clock = new Clock()

  constructor(canvas: HTMLCanvasElement, private params: React.RefObject<TechBlackHoleProps>) {
    this.renderer = new WebGLRenderer({ canvas, alpha: true, antialias: true })
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

    this.scene = new Scene()
    this.camera = new PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 100)
    this.camera.position.z = 1.2

    this.geometry = new PlaneGeometry(4, 4)
    this.material = new ShaderMaterial({
      uniforms: {
        iTime: { value: 0 },
        iResolution: { value: new Vector3(window.innerWidth, window.innerHeight, 1) },
        iOffset: { value: new Vector2(params.current?.offsetX ?? 0.35, params.current?.offsetY ?? -0.25) },
      },
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
    })
    this.scene.add(new Mesh(this.geometry, this.material))
    this.resize()
  }

  resize() {
    const w = window.innerWidth
    const h = window.innerHeight
    // updateStyle:false — the platform keeps the canvas at 100% via CSS.
    this.renderer.setSize(w, h, false)
    const u = this.material.uniforms
    ;(u.iResolution.value as Vector3).set(w, h, 1)
    const p = this.params.current ?? ({} as TechBlackHoleProps)
    if (w < 768) {
      ;(u.iOffset.value as Vector2).set(0.0, 0.35)
    } else {
      ;(u.iOffset.value as Vector2).set(p.offsetX ?? 0.35, p.offsetY ?? -0.25)
    }
  }

  render() {
    this.material.uniforms.iTime.value = this.clock.getElapsedTime()
    this.renderer.render(this.scene, this.camera)
  }

  dispose() {
    this.geometry.dispose()
    this.material.dispose()
    this.renderer.dispose()
    this.renderer.forceContextLoss()
  }
}

export default function TechBlackHole({ offsetX = 0.35, offsetY = -0.25 }: TechBlackHoleProps) {
  const params = useRef<TechBlackHoleProps>({ offsetX, offsetY })
  params.current = { offsetX, offsetY }

  return useGpuEffect<BlackHoleGL>({
    backend: "canvas",
    mode: "pauseAll",
    className: "absolute inset-0 w-full h-full pointer-events-none",
    style: { position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 0, pointerEvents: "none" },
    setup: (ctx) => new BlackHoleGL((ctx as CanvasSetupContext).canvas, params),
    frame: (state) => state.render(),
    resize: (state) => state.resize(),
    teardown: (state) => state.dispose(),
  })
}
